import React, { useCallback, useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";

import type { InsuranceGroupOption } from "~/api/adminProviderSearch";
import {
  getInsuranceGroups,
  resolveGroupFromName,
  staffSearch,
} from "~/api/adminProviderSearch";
import CalendarSelector from "~/shared/components/ui/CalendarSelector";
import ProviderCard from "~/shared/components/ui/ProviderCard";
import RankMetrics from "~/shared/components/ui/RankMetrics";
import { useStaffSearchCalendar } from "~/shared/hooks/useStaffSearchCalendar";
import {
  HEALTH_CONCERNS,
  LOCATION_OPTIONS,
  US_STATES,
  VIDEO_CALL_LOCATION,
} from "~/shared/utils/constants";
import { currentDay, currentDayName, currentMonth } from "~/shared/utils/dates";
import { timezone } from "~/shared/utils/timezone";
import { tw } from "~/utils";
import { isNonProduction } from "../utils/envUtils";

const getOfficeDisplayName = (office: string): string =>
  office === VIDEO_CALL_LOCATION ? "Telehealth" : office;

const formatDate = (date: Date): string => date.toISOString().split("T")[0];

// Stable reference so the group-resolution effect (keyed on insuranceGroups)
// does not re-fire on every render while the query is still pending.
const EMPTY_INSURANCE_GROUPS: InsuranceGroupOption[] = [];

export const AdminProviderSearchScreen: React.FC = () => {
  const [groupId, setGroupId] = useState("");
  const [groupDisplayName, setGroupDisplayName] = useState("");
  const [selectedConditions, setSelectedConditions] = useState<string[]>([]);
  const [stateCode, setStateCode] = useState("NY");
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const { data: insuranceGroups = EMPTY_INSURANCE_GROUPS } = useQuery({
    queryKey: ["admin", "insurance-groups"],
    queryFn: getInsuranceGroups,
  });

  const [searchParams] = useSearchParams();
  const didInitGroupRef = useRef(false);

  // Single-office selector: telehealth + the four NY offices. Non-NY states
  // can only see telehealth (there's no in-person office to offer them).
  const [office, setOffice] = useState<string>(VIDEO_CALL_LOCATION);

  // Reset office when state changes away from NY (non-NY is telehealth only)
  useEffect(() => {
    if (stateCode !== "NY") {
      setOffice(VIDEO_CALL_LOCATION);
    }
  }, [stateCode]);

  // Seed the non-async filters once from the URL (deep-link from booking).
  useEffect(() => {
    const conditions = searchParams.getAll("conditions");
    const state = searchParams.get("state");
    const office = searchParams.get("office");
    if (conditions.length > 0) {
      setSelectedConditions(conditions);
    }
    if (state) {
      setStateCode(state);
    }
    if (office) {
      setOffice(office);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resolve the group once the insurance-groups list has loaded. Runs once;
  // never overrides a manual staff change (guarded by didInitGroupRef).
  useEffect(() => {
    if (didInitGroupRef.current) {
      return;
    }
    const groupName = searchParams.get("group");
    if (!groupName || insuranceGroups.length === 0) {
      return;
    }
    const match = resolveGroupFromName(groupName, insuranceGroups);
    if (match) {
      setGroupId(match.groupId);
      setGroupDisplayName(match.displayName);
    }
    didInitGroupRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insuranceGroups]);

  const canSearch = groupId !== "" && selectedConditions.length > 0;

  const handleFirstAvailableDayFound = useCallback((date: Date | null) => {
    setSelectedDate(date);
  }, []);

  const {
    calendarDays,
    canNavigatePrev,
    navigatePrevWeek,
    navigateNextWeek,
    isLoading: isCalendarLoading,
    isFetching: isCalendarFetching,
    error: calendarError,
    availabilityForNextMonth,
    isMobile,
  } = useStaffSearchCalendar(
    {
      groupId: canSearch ? groupId : "",
      conditions: selectedConditions,
      state: stateCode,
      office,
    },
    handleFirstAvailableDayFound,
  );

  const selectedDateStr = selectedDate ? formatDate(selectedDate) : null;

  const {
    data: providers = [],
    isLoading: isProvidersLoading,
    isFetching: isProvidersFetching,
    error: providersError,
  } = useQuery({
    queryKey: [
      "admin",
      "staff-search",
      groupId,
      selectedConditions,
      stateCode,
      office,
      selectedDateStr,
    ],
    queryFn: () =>
      staffSearch({
        groupId,
        conditions: selectedConditions,
        state: stateCode,
        preferredOfficeVisit: office,
        startDate: selectedDateStr!,
        endDate: selectedDateStr!,
        timezone,
      }),
    enabled: canSearch && !!selectedDateStr,
    placeholderData: (previousData) => previousData,
  });

  const handleDateSelect = useCallback((date: Date) => {
    setSelectedDate(date);
  }, []);

  const handleConditionToggle = (condition: string) => {
    setSelectedConditions((prev) =>
      prev.includes(condition)
        ? prev.filter((c) => c !== condition)
        : [...prev, condition],
    );
  };

  const isResultsLoading =
    isCalendarLoading ||
    isCalendarFetching ||
    isProvidersLoading ||
    isProvidersFetching;

  return (
    <div className="mx-auto max-w-6xl px-4 py-8">
      <h1 className="mb-6 text-2xl font-bold text-gray-900">
        Provider Search Tool
      </h1>

      <div className="mb-8 grid gap-6 rounded-lg border border-gray-200 bg-white p-6 lg:grid-cols-2">
        {/* Insurance Group */}
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-gray-700">
            Insurance Group
          </label>
          <select
            value={groupId}
            onChange={(e) => {
              const id = e.target.value;
              setGroupId(id);
              const match = insuranceGroups.find((g) => g.groupId === id);
              setGroupDisplayName(match?.displayName ?? "");
            }}
            className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-gray-500 focus:outline-none"
          >
            <option value="">Select insurance group...</option>
            {insuranceGroups.map((g) => (
              <option key={g.groupId} value={g.groupId}>
                {g.displayName}
              </option>
            ))}
          </select>
        </div>

        {/* State */}
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-gray-700">State</label>
          <select
            value={stateCode}
            onChange={(e) => {
              setStateCode(e.target.value);
            }}
            className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-gray-500 focus:outline-none"
          >
            {US_STATES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>

        {/* Office */}
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-gray-700">Office</label>
          {stateCode === "NY" ? (
            <select
              value={office}
              onChange={(e) => {
                setOffice(e.target.value);
              }}
              className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-gray-500 focus:outline-none"
            >
              {LOCATION_OPTIONS.map((loc) => (
                <option key={loc} value={loc}>
                  {getOfficeDisplayName(loc)}
                </option>
              ))}
            </select>
          ) : (
            <select
              value={VIDEO_CALL_LOCATION}
              disabled
              className="rounded-lg border border-gray-300 bg-gray-50 px-4 py-2.5 text-sm text-gray-500"
            >
              <option value={VIDEO_CALL_LOCATION}>Telehealth</option>
            </select>
          )}
        </div>

        {/* Health Conditions */}
        <div className="flex flex-col gap-1.5 lg:col-span-2">
          <label className="text-sm font-medium text-gray-700">
            Health Conditions
          </label>
          <div className="flex max-h-48 flex-wrap gap-2 overflow-y-auto rounded-lg border border-gray-200 p-3">
            {HEALTH_CONCERNS.map((condition) => {
              const isSelected = selectedConditions.includes(condition);
              return (
                <button
                  key={condition}
                  type="button"
                  onClick={() => handleConditionToggle(condition)}
                  className={tw(
                    "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                    isSelected
                      ? "border-green-500 bg-green-50 text-green-800"
                      : "border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50",
                  )}
                >
                  {condition}
                </button>
              );
            })}
          </div>
          {selectedConditions.length > 0 && (
            <p className="text-xs text-gray-500">
              {selectedConditions.length} selected:{" "}
              {selectedConditions.join(", ")}
            </p>
          )}
        </div>
      </div>

      {/* Calendar + Results */}
      {canSearch && (
        <div>
          {/* Calendar */}
          <div className="mb-6 rounded-lg border border-gray-200 bg-white p-4">
            <CalendarSelector
              month={currentMonth}
              day={currentDay}
              dayName={currentDayName}
              daysInView={calendarDays}
              onDateSelect={handleDateSelect}
              onNavigatePrev={canNavigatePrev ? navigatePrevWeek : null}
              onNavigateNext={navigateNextWeek}
              selectedDate={selectedDate}
              loading={isCalendarLoading}
              isMobile={isMobile}
            />
          </div>

          {/* Results */}
          {(calendarError || providersError) && (
            <div className="mb-4 text-xs text-red-500">
              Error loading providers
            </div>
          )}

          {/* No eligible provider covers these conditions on this plan at all —
              actionable for staff, who can adjust the search. */}
          {!isCalendarLoading &&
            availabilityForNextMonth === false &&
            selectedConditions.length > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 py-8 text-center text-sm text-amber-800">
                No eligible providers for {selectedConditions.join(", ")} on{" "}
                {groupDisplayName || "this plan"}. Remove a condition or change
                location.
              </div>
            )}

          {!isResultsLoading &&
            availabilityForNextMonth !== false &&
            !selectedDate && (
              <div className="rounded-lg border border-gray-200 bg-white py-12 text-center text-gray-500">
                Select a date to view available providers
              </div>
            )}

          {!isResultsLoading &&
            availabilityForNextMonth !== false &&
            selectedDate &&
            providers.length === 0 &&
            !providersError && (
              <div className="rounded-lg border border-gray-200 bg-white py-12 text-center text-gray-500">
                No providers available on this date. Try another date or adjust
                your preferences.
              </div>
            )}

          {providers.length > 0 && (
            <div>
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">
                  {providers.length} provider
                  {providers.length !== 1 ? "s" : ""} found
                </h2>
              </div>
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {providers.map((provider) => (
                  <div key={provider.id} id={`provider-${provider.id}`}>
                    <RankMetrics
                      sortOrder={provider.sortOrder}
                      isFullMatch={provider.isFullMatch}
                      isFullTime={provider.isFullTime}
                      matchCount={provider.matchCount}
                      conditionCount={selectedConditions.length}
                      bookedMinutes={provider.bookedMinutes}
                      periodCapMinutes={provider.periodCapMinutes}
                      isAtCap={provider.isAtCap}
                      isPrioritized={provider.isPrioritized}
                    />
                    <ProviderCard provider={provider} location={office} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {isNonProduction() && groupId && (
        <p className="mt-4 text-xs text-gray-400">
          Group: {groupDisplayName} ({groupId})
        </p>
      )}
    </div>
  );
};
